import { Trans, useLingui } from '@lingui/react/macro';

import AlertBar from '@/design_system/AlertBar';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import IconRefusal from '@/icons/Refusal.svg';
import IconValidation from '@/icons/Validation.svg';
import { computeWarrantyExpirationDate, WARRANTY_OVERRIDE_REASONS } from '@/models/article';
import { type ArticleWithRelations } from '@/models/request';
import FusalpWarrantyModal from '@/routes/Brand/Requests/New/components/Article/components/ArticleForm/components/FusalpWarrantyModal';
import { useCurrentOrganization } from '@/services/auth';

export const WarrantyAlert = ({
  article,
  isDiagForm = false,
}: {
  article: ArticleWithRelations;
  isDiagForm?: boolean;
}) => {
  const { i18n } = useLingui();
  const [organization] = useCurrentOrganization();

  if (!article.activeWarrantyType && !article.supplierWarrantyExpirationDate) {
    return (
      <AlertBar>
        <Trans id="article.form.warranty.alert.add-purchase-info">
          To benefit from warranty, please add the item purchase information
        </Trans>
        {isDiagForm && organization && warrantyInfoForOrganization[organization.slug] && (
          <>. {warrantyInfoForOrganization[organization.slug]}</>
        )}
      </AlertBar>
    );
  }

  if (article.activeWarrantyType === 'supplier' && article.supplierWarrantyExpirationDate) {
    const {
      activeWarrantyType,
      supplierWarrantyExpirationDate,
      hasDistributorWarrantyExtension,
      distributorWarrantyExpirationDate,
    } = article;

    // For some reason, Typescript can't infer article type from the "if" condition, so I have to destructure all properties
    const date = computeWarrantyExpirationDate({
      activeWarrantyType,
      supplierWarrantyExpirationDate,
      hasDistributorWarrantyExtension,
      distributorWarrantyExpirationDate,
    });

    return (
      <AlertBar type="success" icon={<IconValidation />}>
        <Trans id="article.form.warranty.alert.warranty-applicable.with-expiration-date">
          Warranty applicable until <InlineText weight="medium">{date}</InlineText>
        </Trans>
      </AlertBar>
    );
  }

  if (article.activeWarrantyType === 'distributor' && article.distributorWarrantyExpirationDate) {
    const { activeWarrantyType, distributorWarrantyExpirationDate } = article;

    // For some reason, Typescript can't infer article type from the "if" condition, so I have to destructure all properties
    const date = computeWarrantyExpirationDate({
      activeWarrantyType,
      distributorWarrantyExpirationDate,
    });

    return (
      <AlertBar type="success" icon={<IconValidation />}>
        <Trans id="article.form.warranty.alert.warranty-applicable.with-expiration-date">
          Warranty applicable until <InlineText weight="medium">{date}</InlineText>
        </Trans>
      </AlertBar>
    );
  }

  if (article.activeWarrantyType === 'override') {
    const warrantyOverrideReason = article.warrantyOverrideReason
      ? WARRANTY_OVERRIDE_REASONS.find((reason) => reason.id === article.warrantyOverrideReason)
      : undefined;
    const warrantyOverrideLabel = warrantyOverrideReason
      ? i18n._(warrantyOverrideReason.text)
      : undefined;

    return (
      <AlertBar type="success" icon={<IconValidation />}>
        <Stack>
          <Trans id="article.form.warranty.alert.warranty-applicable.without-expiration-date">
            Warranty applicable
          </Trans>
          {!!warrantyOverrideLabel && (
            <InlineText size="200" color="secondary">
              <Trans id="article.form.warranty.alert.warranty-applicable.without-expiration-date.reason.label">
                Reason:
              </Trans>{' '}
              {warrantyOverrideLabel}
            </InlineText>
          )}
        </Stack>
      </AlertBar>
    );
  }

  return (
    <AlertBar type="warning" icon={<IconRefusal />}>
      <Trans id="article.form.warranty.alert.warranty-not-applicable">Out of warranty</Trans>
    </AlertBar>
  );
};

const warrantyInfoForOrganization: Record<string, React.ReactNode> = {
  fusalp: <FusalpWarrantyModal />,
};
